/**
 * One way to ask "are you sure".
 *
 * `window.confirm` is a native OS dialog in the middle of an OKLCH-themed app,
 * and going straight from one click to gone is worse. This is the third option,
 * and it is the only one.
 *
 * IMPERATIVE, not a component. Every call site already deletes inside an
 * `onClick`, so `if (await confirm({...}))` is a one-line change there. A
 * component form would need new state and JSX at each of them, which is how
 * "add a confirmation" turns into a refactor nobody does.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Button } from './button'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from './overlays'

export interface ConfirmOptions {
  title: string
  /** The consequence, in the user's terms. Skip it when the title says everything. */
  body?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'danger' | 'default'
}

type Ask = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<Ask | null>(null)

/**
 * Mounted once, beside `TooltipProvider`. A dialog per call site would stack
 * portals and leave the page with several backdrops fighting over focus.
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null)
  const resolver = useRef<((ok: boolean) => void) | null>(null)
  const confirmButton = useRef<HTMLButtonElement>(null)

  /**
   * Move focus into the dialog ourselves.
   *
   * `autoFocus` alone loses a race when the confirm is opened from a Menu:
   * the menu restores focus to its trigger AFTER the dialog mounts, so focus
   * ends up back on the menu item. Measured, not guessed — the dialog opened
   * with `document.activeElement` still on "Delete chat", which meant Escape
   * went to the menu and the dialog could not be dismissed by keyboard at all.
   *
   * Two frames: one for the dialog to mount, one for the menu to finish
   * putting focus back, so this lands last.
   */
  useEffect(() => {
    if (!options) return
    const frame = requestAnimationFrame(() =>
      requestAnimationFrame(() => confirmButton.current?.focus())
    )
    return () => cancelAnimationFrame(frame)
  }, [options])

  const ask = useCallback<Ask>((next) => {
    setOptions(next)
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve
    })
  }, [])

  // Closing by ANY route is a "no": Escape, the backdrop and the X all land
  // here, and a promise left unresolved would hang the caller's handler
  // forever rather than simply not deleting.
  const settle = (ok: boolean) => {
    resolver.current?.(ok)
    resolver.current = null
    setOptions(null)
  }

  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      <Dialog open={options !== null} onOpenChange={(open) => !open && settle(false)}>
        <DialogContent size="sm">
          <DialogHeader title={options?.title ?? ''} />
          {options?.body ? <DialogBody className="text-xs text-[var(--color-fg-muted)]">{options.body}</DialogBody> : null}
          <DialogFooter>
            <Button size="sm" variant="ghost" onClick={() => settle(false)}>
              {options?.cancelLabel ?? 'Cancel'}
            </Button>
            <Button
              ref={confirmButton}
              size="sm"
              variant={options?.tone === 'danger' ? 'danger' : 'primary'}
              onClick={() => settle(true)}
            >
              {options?.confirmLabel ?? 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  )
}

/**
 * `const confirm = useConfirm()` → `if (!(await confirm({...}))) return`.
 *
 * Throws when the provider is missing rather than silently resolving true: a
 * confirmation that always says yes is worse than none, because the call site
 * reads as guarded.
 */
export function useConfirm(): Ask {
  const ask = useContext(ConfirmContext)
  if (!ask) throw new Error('useConfirm requires <ConfirmProvider>')
  return ask
}
